import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const tips = [
  "视频时长建议控制在 10 分钟以内，节奏更轻快。",
  "封面清晰、标题具体，更容易被推荐给同好。",
  "请确保内容为原创或已获得授权，共同维护社区氛围。",
];

export default function UploadPage() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);

  useSeo({
    title: "上传视频 - 呱呱视频社区",
    description: "在呱呱视频社区上传你的视频作品，分享给更多志同道合的观众，零门槛参与社区创作。",
    keywords: "上传视频,呱呱视频社区,视频投稿,分享视频",
  });

  const validate = () => {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = "请填写视频标题";
    else if (title.trim().length > 60) next.title = "标题最多 60 个字";
    if (!category) next.category = "请选择视频分类";
    if (!fileName) next.file = "请选择要上传的视频文件";
    if (description.length > 500) next.description = "简介最多 500 个字";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setDone(true);
  };

  if (done) {
    return (
      <div className="max-w-[720px] mx-auto px-4 md:px-6 py-12">
        <Breadcrumb items={[{ label: "上传视频" }]} />
        <div className="mt-6 p-8 rounded-lg bg-background-50 border border-background-200 text-center animate-scale-in">
          <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-checkbox-circle-fill text-4xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-4 font-heading font-extrabold text-xl text-foreground-950">上传成功！</h1>
          <p className="mt-2 text-sm text-foreground-600">
            《{title}》已提交，审核通过后就会出现在社区中。（本页为前端演示，不会真正传输文件）
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setDone(false);
                setTitle("");
                setCategory("");
                setDescription("");
                setTags("");
                setFileName("");
                if (fileRef.current) fileRef.current.value = "";
              }}
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" aria-hidden="true"></i>
              继续上传
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line" aria-hidden="true"></i>
              返回首页
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
      <Breadcrumb items={[{ label: "上传视频" }]} />
      <div className="mt-5 flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="flex-1 min-w-0">
          <h1 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">上传你的视频</h1>
          <p className="mt-1 text-sm text-foreground-500">填写下面的信息，把你的作品分享给更多同好。</p>

          <form onSubmit={submit} className="mt-6 p-5 md:p-6 rounded-lg bg-background-50 border border-background-200 space-y-5">
            <div>
              <label htmlFor="up-title" className="block text-sm font-medium text-foreground-800 mb-2">
                视频标题 <span className="text-accent-600">*</span>
              </label>
              <input
                id="up-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={60}
                placeholder="一句话说清楚视频内容，例如：三分钟学会做手冲咖啡"
                className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400"
              />
              {errors.title && <p className="mt-1 text-xs text-accent-600">{errors.title}</p>}
            </div>

            <div>
              <label htmlFor="up-cat" className="block text-sm font-medium text-foreground-800 mb-2">
                所属分类 <span className="text-accent-600">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => setCategory(cat.slug)}
                    className={`flex items-center gap-2 h-12 px-4 rounded-md border text-sm cursor-pointer transition-colors ${
                      category === cat.slug
                        ? "border-primary-500 bg-primary-50 text-primary-700 font-semibold"
                        : "border-background-200 bg-background-100 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    <i className={cat.icon} aria-hidden="true"></i>
                    {cat.name}
                  </button>
                ))}
              </div>
              {errors.category && <p className="mt-1 text-xs text-accent-600">{errors.category}</p>}
            </div>

            <div>
              <label htmlFor="up-desc" className="block text-sm font-medium text-foreground-800 mb-2">视频简介</label>
              <textarea
                id="up-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="简单介绍一下视频内容（最多 500 字）"
                className="w-full px-3 py-2 rounded-md bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400 resize-none"
              />
              <div className="mt-1 flex items-center justify-between">
                {errors.description ? (
                  <p className="text-xs text-accent-600">{errors.description}</p>
                ) : (
                  <span className="text-xs text-foreground-400">好的简介更容易被推荐</span>
                )}
                <span className={`text-xs ${description.length > 480 ? "text-accent-600" : "text-foreground-400"}`}>
                  {description.length}/500
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="up-tags" className="block text-sm font-medium text-foreground-800 mb-2">标签</label>
              <input
                id="up-tags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="用逗号分隔，例如：露营, 户外, vlog"
                className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                视频文件 <span className="text-accent-600">*</span>
              </label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-md border border-dashed border-background-300 bg-background-100 text-foreground-500 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-upload-cloud-2-line text-3xl" aria-hidden="true"></i>
                <span className="text-sm">{fileName || "点击选择视频文件（演示，不会真正上传）"}</span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => setFileName(e.target.files?.[0]?.name ?? "")}
              />
              {errors.file && <p className="mt-1 text-xs text-accent-600">{errors.file}</p>}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-1.5 h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-upload-2-line" aria-hidden="true"></i>
                提交上传
              </button>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-1.5 h-11 px-6 rounded-full border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                取消
              </Link>
            </div>
          </form>
        </div>

        <aside className="w-full lg:w-[320px] shrink-0">
          <div className="rounded-lg bg-background-100 border border-background-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-lightbulb-line text-primary-500 text-lg" aria-hidden="true"></i>
              <h2 className="font-heading font-bold text-foreground-950">上传小贴士</h2>
            </div>
            <ul className="space-y-3">
              {tips.map((tip) => (
                <li key={tip} className="flex gap-2 text-sm text-foreground-600 leading-relaxed">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5" aria-hidden="true"></i>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 p-3 rounded-md bg-accent-50 text-accent-800 text-xs leading-relaxed">
              温馨提示：本站为演示项目，上传不会真正传输文件，也不会对外发布内容。
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}